import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { IntelCtx } from "@/components/intel/context";
import { CopilotSheet } from "@/components/intel/CopilotSheet";
import { SignalSheet } from "@/components/intel/SignalSheet";
import { CreateOpportunityDialog } from "@/components/intel/CreateOpportunityDialog";

export { useIntel } from "@/components/intel/context";

/**
 * Layout route for /intel/*. Owns the shared overlays (Copilot, Signal deep-dive, Create opportunity) so every
 * page can open them with one call and never loses its own context.
 */
export function IntelLayout() {
  const [copilot, setCopilot] = useState(null);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [signal, setSignal] = useState(null);
  const [signalOpen, setSignalOpen] = useState(false);
  const [create, setCreate] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [version, setVersion] = useState(0);
  const [config, setConfig] = useState(null);
  const [dismissed, setDismissed] = useState(() => new Set());

  useEffect(() => {
    api.get("/intel/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const openCopilot = useCallback((view, tab = "brief", extra = {}) => {
    const c = view.company || {};
    setCopilot({ companyId: view.company_id || c.id, projectId: view.project_id || null, companyName: c.name, projectName: view.project_name, language: c.language || "en", roles: view.roles || [], tab, ...extra });
    setCopilotOpen(true);
  }, []);

  const openSignal = useCallback((view) => {
    setSignal({ projectId: view.project_id || view.id, initialView: view.signals ? view : null });
    setSignalOpen(true);
  }, []);

  const openCreate = useCallback((view) => {
    setCreate(view);
    setCreateOpen(true);
  }, []);

  const dismiss = useCallback(async (view) => {
    const pid = view.project_id || view.id;
    // optimistic: hide immediately, confirm with the API, offer undo
    setDismissed((s) => new Set(s).add(pid));
    try {
      await api.post("/intel/dismiss", { project_id: pid, reason: "" });
      bump();
      toast("Opportunity dismissed", {
        description: `${view.company?.name || "Company"} · ${view.project_name || ""}`,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/intel/dismiss/${pid}`);
              setDismissed((s) => { const n = new Set(s); n.delete(pid); return n; });
              bump();
            } catch (e) {
              toast.error(errMsg(e));
            }
          },
        },
      });
    } catch (e) {
      setDismissed((s) => { const n = new Set(s); n.delete(pid); return n; });
      toast.error(errMsg(e));
    }
  }, [bump]);

  const signalTypes = useMemo(() => {
    const out = {};
    Object.entries(config?.signal_types || {}).forEach(([k, v]) => { out[k] = v.label || k; });
    return out;
  }, [config]);

  const value = useMemo(() => ({ openCopilot, openSignal, openCreate, dismiss, dismissed, bump, version, config, signalTypes, stages: config?.pipeline_stages }), [openCopilot, openSignal, openCreate, dismiss, dismissed, bump, version, config, signalTypes]);

  return (
    <IntelCtx.Provider value={value}>
      <Outlet />
      <CopilotSheet open={copilotOpen} onOpenChange={setCopilotOpen} ctx={copilot} onActivity={bump} />
      <SignalSheet open={signalOpen} onOpenChange={setSignalOpen} projectId={signal?.projectId} initialView={signal?.initialView} />
      <CreateOpportunityDialog open={createOpen} onOpenChange={setCreateOpen} view={create} onCreated={bump} />
    </IntelCtx.Provider>
  );
}
